import { Box, Grid, IconButton, Tab, Tabs } from "@material-ui/core";
import { DialogWrapper } from "components/shared/dialog";
import React, { useState } from "react";
import { UploadVideoButton } from "./upload-video-button";
import { AddEmbedVideo } from "./add-embed-video";
import { useDispatch, useSelector } from "react-redux";
import {
  GetUrlBuilderData,
  SetUrlBuilderData,
  UploadType,
} from "store/url-builder";
import { postVideo } from "adapters";
import ReactPlayer from "react-player";
import HighlightOffOutlined from "@material-ui/icons/HighlightOffOutlined";
import { theme } from "material-theme";
import { useParams } from "react-router-dom";
import { GetConfigData } from "store/config/selectors";
import { ResourceType } from "enums/videoType";
import { GetCardData } from "store/card";

export interface AddVideoDialogProps {
  open: boolean;
  onClose(): void;
  builderType: string;
  duplicateId?: string;
  initialVideo?: { actionType: any; file?: any; videoUrl?: string };
}

export const AddVideoDialog = ({
  onClose,
  open,
  builderType,
  duplicateId,
  initialVideo,
}: AddVideoDialogProps) => {
  const builderData = useSelector(GetUrlBuilderData);
  const cardData = useSelector(GetCardData);
  const confData = useSelector(GetConfigData);
  const [loading, setLoading] = useState(false);

  const isDuplicateSection = Boolean(duplicateId);
  // For duplicates, only use the duplicate data; do not fall back to main video
  const videoData = isDuplicateSection ? initialVideo : builderData.video;

  const [video, setVideo] = useState<UploadType>(
    (videoData?.file as UploadType) || (isDuplicateSection ? (null as any) : builderData.video.file)
  );
  const [embededVideoUrl, setEmbededVideoUrl] = useState<string>(
    videoData?.videoUrl || (isDuplicateSection ? "" : builderData.video.videoUrl) || ""
  );

  const [disableSave, setDisableSave] = useState(false);

  const [tab, setTab] = useState(
    videoData?.actionType === ResourceType.Regular ? 0 : 1
  );
  const dispatch = useDispatch();
  const { cardId }: { cardId: string } = useParams();

  const handleChange = (event: React.ChangeEvent<{}>, newValue: number) =>
    setTab(newValue);

  const handleSubmit = () => {
    setLoading(true);
    const videoPayload = {
      file: tab === 0 ? { _id: video._id } : null,
      videoUrl: tab === 1 ? embededVideoUrl : "",
      actionType: tab === 0 ? ResourceType.Regular : ResourceType.Embeded,
    };
    
    // If it's a duplicate section, include the duplicateId in the payload
    const payload = duplicateId 
      ? { ...videoPayload, _duplicateId: duplicateId }
      : videoPayload;

    postVideo(
      cardId,
      builderType,
      payload,
      cardData?.templateId?.toString() || ""
    )
      .then(({ data }) => {
        if (!data.success) return;
        dispatch(SetUrlBuilderData(data.data));
        onClose();
      })
      .finally(() => setLoading(false));
  };

  const removeVideo = () => {
    const response = window.confirm(
      "Are you sure you want to remove the video?"
    );
    if (response)
      setVideo({
        _id: "0",
        fileName: "",
        filePath: "",
        mimeType: "",
        createdAt: "",
        updatedAt: "",
        __v: 0,
      });
  };

  return (
    <DialogWrapper
      title="Video"
      onClose={(event, reason) => {
        if (reason && reason === "backdropClick") return false;
        else onClose();
      }}
      open={open}
      onSubmit={handleSubmit}
      loading={loading}
      disableSave={disableSave}
    >
      <Grid item xs={12} className="mb-3">
        <Tabs
          value={tab}
          indicatorColor="primary"
          textColor="primary"
          onChange={handleChange}
          variant="fullWidth"
        >
          <Tab label="Upload Video" />
          <Tab label="Embed Video" />
        </Tabs>
      </Grid>
      {tab === 0 && (
        <Grid item xs={12}>
          {video && video._id !== "0" && (
            <Box position="relative">
              <Box position="absolute" top="5px" right="5px" zIndex={200}>
                <IconButton
                  onClick={removeVideo}
                  style={{ color: theme.palette.error.main }}
                >
                  <HighlightOffOutlined />
                </IconButton>
              </Box>
              <ReactPlayer
                url={
                  confData.filesBaseUrl +
                  confData.filesBasePath +
                  "/" +
                  video.fileName
                }
                controls
                width="100%"
              />
            </Box>
          )}
          {(!video || video._id === "0") && (
            <UploadVideoButton
              onUpload={setVideo}
              setDisableSave={setDisableSave}
            />
          )}
        </Grid>
      )}
      {tab === 1 && (
        <Grid item xs={12}>
          <AddEmbedVideo
            embededVideoUrl={embededVideoUrl}
            setEmbededVideoUrl={setEmbededVideoUrl}
          />
        </Grid>
      )}
    </DialogWrapper>
  );
};
